
import React from 'react';
import Box from '../../components/Common/Box';
import Disclaimer from '../../components/Common/Disclaimer';
import { SwingOpportunity, SellSignal } from './useSwingTrader';

interface DesktopSwingProps {
    data: any;
    activeTab: string;
    setActiveTab: (tab: any) => void;
    strategy: 'swing' | 'trend';
    setStrategy: (strat: 'swing' | 'trend') => void;
    opportunities: SwingOpportunity[];
    sellSignals: SellSignal[];
    eligibleUniverse: any[];
    sentiment: string;
    sentimentColors: any;
    navigate: (path: string) => void;
}

const DesktopSwing: React.FC<DesktopSwingProps> = ({
    data,
    activeTab,
    setActiveTab,
    strategy,
    setStrategy,
    opportunities,
    sellSignals,
    eligibleUniverse,
    sentiment,
    sentimentColors,
    navigate
}) => {
    const summary = data?.portfolio_summary;

    return (
        <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
            <Disclaimer type="investment" />

            {/* Portfolio Summary */}
            <Box>
                <div style={styles.summaryGrid}>
                    <div style={styles.summaryItem}>
                        <span style={styles.summaryLabel}>💰 Cash disponible</span>
                        <span style={styles.summaryValue}>{summary?.cash_available?.toLocaleString()} FCFA</span>
                    </div>
                    <div style={styles.summaryItem}>
                        <span style={styles.summaryLabel}>⏱️ Bloqué J+2</span>
                        <span style={styles.summaryValueSmall}>{summary?.cash_blocked_j2?.toLocaleString()} FCFA</span>
                    </div>
                    <div style={styles.summaryItem}>
                        <span style={styles.summaryLabel}>📊 Positions ouvertes</span>
                        <span style={styles.summaryValue}>{summary?.open_swing_positions}</span>
                    </div>
                    <div style={styles.summaryItem}>
                        <span style={styles.summaryLabel}>💼 Max par position</span>
                        <span style={styles.summaryValueSmall}>{summary?.max_position_value?.toLocaleString()} FCFA</span>
                    </div>
                    <div style={styles.summaryItem}>
                        <span style={styles.summaryLabel}>📈 Sentiment marché</span>
                        <span style={{
                            ...styles.badge,
                            backgroundColor: sentimentColors[sentiment].bg,
                            color: sentimentColors[sentiment].color,
                        }}>
                            {sentiment === 'BULLISH' ? '🟢 Haussier' : sentiment === 'BEARISH' ? '🔴 Baissier' : '⚪ Neutre'}
                        </span>
                    </div>
                </div>
            </Box>

            {/* Tabs */}
            <div style={styles.tabContainer}>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'opportunities' ? styles.tabActive : {}) }}
                    onClick={() => setActiveTab('opportunities')}
                >
                    🔥 Configurations Haussières ({opportunities.length})
                </button>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'sell' ? styles.tabActiveSell : {}) }}
                    onClick={() => setActiveTab('sell')}
                >
                    📉 Alertes Baissières ({sellSignals.length})
                </button>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'universe' ? styles.tabActiveUniverse : {}) }}
                    onClick={() => setActiveTab('universe')}
                >
                    🌍 Univers Éligible ({eligibleUniverse.length})
                </button>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'info' ? styles.tabActiveInfo : {}) }}
                    onClick={() => setActiveTab('info')}
                >
                    ℹ️ Aide 
                </button>
            </div>

            {/* Opportunities */}
            {activeTab === 'opportunities' && (
                <div style={styles.cardsGrid}>
                    {opportunities.length === 0 ? (
                        <Box>
                            <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                                <p>Aucune configuration haussière détectée actuellement.</p>
                            </div>
                        </Box>
                    ) : (
                        opportunities.map((opp) => (
                            <Box key={opp.symbole}>
                                <div style={styles.card}>
                                    <div style={styles.cardHeader}>
                                        <div>
                                            <span style={styles.symbolLink} onClick={() => navigate(`/data?symbol=${opp.symbole}`)}>
                                                {opp.symbole}
                                            </span>
                                            <p style={styles.companyName}>{opp.nom_societe}</p>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ ...styles.scoreBadge, backgroundColor: opp.swing_score >= 80 ? '#16a34a' : '#d97706' }}>
                                                {opp.swing_score}
                                            </div>
                                            <span style={{ fontSize: 11, color: '#6b7280' }}>Score</span>
                                        </div>
                                    </div>

                                    <div style={styles.priceGrid}>
                                        <div>
                                            <span style={styles.priceLabel}>Entrée</span>
                                            <span style={styles.priceValue}>{opp.entry_price.toLocaleString()}</span>
                                        </div>
                                        <div>
                                            <span style={styles.priceLabel}>Objectif</span>
                                            <span style={{ ...styles.priceValue, color: '#16a34a' }}>{opp.target_price.toLocaleString()}</span>
                                        </div>
                                        <div>
                                            <span style={styles.priceLabel}>Stop</span>
                                            <span style={{ ...styles.priceValue, color: '#dc2626' }}>{opp.stop_loss.toLocaleString()}</span>
                                        </div>
                                    </div>

                                    <div style={styles.positionInfo}>
                                        <span>📦 {opp.position_size} actions</span>
                                        <span>💰 {opp.position_value.toLocaleString()} FCFA</span>
                                        <span>⏱️ {opp.holding_period}</span>
                                    </div>

                                    <div style={styles.technicals}>
                                        {opp.technical.rsi_14 && <span style={styles.techBadge}>RSI: {opp.technical.rsi_14}</span>}
                                        {opp.technical.variation_5d && <span style={styles.techBadge}>5j: {opp.technical.variation_5d}%</span>}
                                    </div>

                                    <p style={styles.justification}>{opp.justification}</p>
                                </div>
                            </Box>
                        ))
                    )}
                </div>
            )}

            {/* Sell Signals */}
            {activeTab === 'sell' && (
                <div style={styles.cardsGrid}>
                    {sellSignals.length === 0 ? (
                        <Box>
                            <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                                <p>Aucune alerte de retournement.</p>
                            </div>
                        </Box>
                    ) : (
                        sellSignals.map((signal) => (
                            <Box key={signal.symbole}>
                                <div style={styles.card}>
                                    <div style={styles.cardHeader}>
                                        <div>
                                            <span style={styles.symbolLink} onClick={() => navigate(`/data?symbol=${signal.symbole}`)}>
                                                {signal.symbole}
                                            </span>
                                            <p style={styles.companyName}>{signal.nom_societe}</p>
                                        </div>
                                        <span style={{ ...styles.badge, backgroundColor: signal.signal_strength === 'STRONG' ? '#fee2e2' : '#fef3c7', color: signal.signal_strength === 'STRONG' ? '#dc2626' : '#d97706' }}>
                                            {signal.signal_strength === 'STRONG' ? '⚠️ Fort' : '⚡ Modéré'}
                                        </span>
                                    </div>

                                    <div style={styles.pnlInfo}>
                                        <div>
                                            <span style={styles.priceLabel}>P&L</span>
                                            <span style={{ ...styles.priceValue, color: signal.unrealized_pnl_pct >= 0 ? '#16a34a' : '#dc2626' }}>
                                                {signal.unrealized_pnl_pct.toFixed(1)}%
                                            </span>
                                        </div>
                                    </div>

                                    <p style={styles.justification}>{signal.justification}</p>

                                    <div style={styles.actionRow}>
                                        <span style={{ ...styles.actionBadge, backgroundColor: signal.recommended_action === 'SELL_ALL' ? '#dc2626' : '#f97316', color: 'white' }}>
                                            {signal.recommended_action === 'SELL_ALL' ? 'Vendre tout' : 'Vendre 50%'}
                                        </span>
                                    </div>
                                </div>
                            </Box>
                        ))
                    )}
                </div>
            )}

            {/* Universe */}
            {activeTab === 'universe' && (
                <Box>
                    <div style={styles.strategyToggle}>
                        <span style={{ fontWeight: 600, marginRight: 12 }}>Stratégie:</span>
                        <button
                            style={{ ...styles.strategyBtn, ...(strategy === 'swing' ? styles.strategyBtnActive : {}) }}
                            onClick={() => setStrategy('swing')}
                        >
                            ⚡ Swing (Rebond)
                        </button>
                        <button
                            style={{ ...styles.strategyBtn, ...(strategy === 'trend' ? styles.strategyBtnActiveTrend : {}) }}
                            onClick={() => setStrategy('trend')}
                        >
                            🏄 Trend Surfer
                        </button>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                        <table style={styles.universeTable}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Symbole</th>
                                    <th style={styles.th}>Nom</th>
                                    <th style={styles.thCenter}>Cours</th>
                                    <th style={styles.thCenter}>Score</th>
                                    <th style={styles.thCenter}>RSI</th>
                                    <th style={styles.thCenter}>Var.</th>
                                    <th style={styles.th}>Analyse</th>
                                    <th style={styles.thCenter}>Statut</th>
                                </tr>
                            </thead>
                            <tbody>
                                {eligibleUniverse.map((stock) => {
                                    const score = strategy === 'swing' ? stock.swing_score : stock.trend_surfer_score;
                                    const analysis = strategy === 'swing' ? stock.swing_analysis : stock.trend_analysis;
                                    const isOpportunity = strategy === 'swing' ? stock.is_swing_opportunity : stock.is_trend_opportunity;

                                    return (
                                        <tr key={stock.symbole} style={{ backgroundColor: isOpportunity ? '#dcfce7' : stock.is_owned ? '#e0f2fe' : 'transparent' }}>
                                            <td style={styles.td}><span style={styles.symbolLink} onClick={() => navigate(`/data?symbol=${stock.symbole}`)}>{stock.symbole}</span></td>
                                            <td style={styles.td}>{stock.nom_societe}</td>
                                            <td style={styles.tdCenter}>{stock.cours_actuel?.toLocaleString()}</td>
                                            <td style={styles.tdCenter}>
                                                <span style={{ ...styles.scorePill, backgroundColor: score >= 75 ? '#16a34a' : '#d97706' }}>{score}</span>
                                            </td>
                                            <td style={styles.tdCenter}>{stock.technical.rsi_14?.toFixed(0)}</td>
                                            <td style={styles.tdCenter}>{stock.technical.variation_5d}%</td>
                                            <td style={styles.tdAnalysis}>{analysis}</td>
                                            <td style={styles.tdCenter}>{isOpportunity ? '🔥' : '—'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Box>
            )}

            {/* Info */}
            {activeTab === 'info' && (
                <Box>
                    <div style={{ padding: 20 }}>
                        <h3>📊 Comment fonctionne le Swing Trader ?</h3>
                        <p>Identification d'opportunités à court terme (3-10 jours) basées sur l'analyse technique (RSI, Momentum, Volume).</p>
                    </div>
                </Box>
            )}
        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16, padding: 20 },
    summaryItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' },
    summaryLabel: { fontSize: 12, color: '#6b7280', marginBottom: 4 },
    summaryValue: { fontSize: 20, fontWeight: 'bold', color: '#1e293b' },
    summaryValueSmall: { fontSize: 16, fontWeight: '600', color: '#475569' },
    badge: { padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: '600' },
    tabContainer: { display: 'flex', gap: 8, marginBottom: 20, marginTop: 20 },
    tab: { flex: 1, padding: '12px 16px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: '600', cursor: 'pointer', backgroundColor: '#f1f5f9', color: '#475569' },
    tabActive: { backgroundColor: '#16a34a', color: 'white' },
    tabActiveSell: { backgroundColor: '#dc2626', color: 'white' },
    tabActiveUniverse: { backgroundColor: '#7c3aed', color: 'white' },
    tabActiveInfo: { backgroundColor: '#2563eb', color: 'white' },
    cardsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: 16 },
    card: { padding: 16, backgroundColor: 'var(--bg-box)', borderRadius: 12, border: '1px solid var(--border-color)' },
    cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
    symbolLink: { fontSize: 18, fontWeight: 'bold', color: '#2563eb', cursor: 'pointer' },
    companyName: { fontSize: 13, color: '#6b7280', margin: '4px 0 0 0' },
    scoreBadge: { width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: 16 },
    priceGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12, padding: 12, backgroundColor: '#f8fafc', borderRadius: 8 },
    priceLabel: { display: 'block', fontSize: 11, color: '#6b7280', marginBottom: 2 },
    priceValue: { display: 'block', fontSize: 14, fontWeight: '600', color: '#1e293b' },
    positionInfo: { display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#475569', marginBottom: 12, padding: '8px 0', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' },
    technicals: { display: 'flex', gap: 8, marginBottom: 12 },
    techBadge: { padding: '2px 8px', backgroundColor: '#e0f2fe', color: '#0369a1', borderRadius: 4, fontSize: 11, fontWeight: '500' },
    justification: { fontSize: 13, color: '#475569', margin: 0, fontStyle: 'italic' },
    pnlInfo: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12, padding: 12, backgroundColor: '#f8fafc', borderRadius: 8 },
    actionRow: { marginTop: 12, textAlign: 'center' },
    actionBadge: { padding: '8px 16px', borderRadius: 6, fontSize: 14, fontWeight: '600' },
    strategyToggle: { display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #e5e7eb', gap: 8 },
    strategyBtn: { padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13, cursor: 'pointer', backgroundColor: 'white' },
    strategyBtnActive: { backgroundColor: '#2563eb', color: 'white', borderColor: '#2563eb' },
    strategyBtnActiveTrend: { backgroundColor: '#7c3aed', color: 'white', borderColor: '#7c3aed' },
    universeTable: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { textAlign: 'left', padding: '12px 8px', borderBottom: '2px solid #e5e7eb', fontWeight: 600, color: '#374151' },
    thCenter: { textAlign: 'center', padding: '12px 8px', borderBottom: '2px solid #e5e7eb', fontWeight: 600, color: '#374151' },
    td: { padding: '10px 8px', borderBottom: '1px solid #f3f4f6' },
    tdCenter: { padding: '10px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'center' },
    tdAnalysis: { padding: '10px 8px', borderBottom: '1px solid #f3f4f6', fontSize: 12, color: '#475569', maxWidth: 300 },
    scorePill: { display: 'inline-block', padding: '2px 8px', borderRadius: 12, color: 'white', fontWeight: 600, fontSize: 12 },
};

export default DesktopSwing;
